import React from "react";
import { resolveImageUrl } from "../context/SiteContext";

/**
 * Renders a logo silhouette filled with a brushed-silver gradient —
 * uses CSS masking so it works in screenshots / PDF exports.
 *
 * Falls back to a plain white-filtered logo if mask CSS is unsupported.
 */
export default function SilverLogo({ src, alt = "", size, style, variant = "silver", className }) {
  if (!src) return null;
  const url = resolveImageUrl(src);
  const w = size?.width ?? size?.w ?? "1in";
  const h = size?.height ?? size?.h ?? "1in";

  // Polished, slightly cool brushed-silver gradient with a soft highlight.
  const silverGradient =
    "linear-gradient(135deg, #8E939B 0%, #C9CDD2 18%, #F4F5F7 32%, #DDE0E4 48%, #A5AAB1 60%, #E8EAED 78%, #B0B5BC 100%)";

  // Warm champagne-silver alt (luxe + warm). Not used by default but kept here for future.
  const champagne =
    "linear-gradient(135deg, #A8A29E 0%, #E4DED4 30%, #F5F0E6 50%, #CBC6BB 70%, #B5B0A4 100%)";

  // Rose gold / warm copper — pairs beautifully with midnight navy.
  const rosegold =
    "linear-gradient(135deg, #8B5E48 0%, #C68866 18%, #E5B499 35%, #F5D5BD 50%, #D69A78 65%, #B07A5E 82%, #8E5A40 100%)";

  // True gold for premium / holiday accents.
  const gold =
    "linear-gradient(135deg, #8E6F2D 0%, #C9A24B 20%, #F2D77A 40%, #FFE7A0 55%, #D4A845 75%, #9C7A2E 100%)";

  const palette = { silver: silverGradient, champagne, rosegold, gold };
  const bg = palette[variant] || silverGradient;

  return (
    <div
      role="img"
      aria-label={alt}
      className={className}
      style={{
        width: w,
        height: h,
        backgroundImage: bg,
        WebkitMaskImage: `url("${url}")`,
        WebkitMaskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        WebkitMaskSize: "contain",
        maskImage: `url("${url}")`,
        maskRepeat: "no-repeat",
        maskPosition: "center",
        maskSize: "contain",
        // 1px white outline around the logo silhouette (cardinal drop-shadows) + subtle highlight
        filter: `
          drop-shadow(1px 0 0 #FDFBF7)
          drop-shadow(-1px 0 0 #FDFBF7)
          drop-shadow(0 1px 0 #FDFBF7)
          drop-shadow(0 -1px 0 #FDFBF7)
          drop-shadow(0 0 0.6px rgba(255,255,255,0.5))
        `,
        ...style,
      }}
    />
  );
}
